import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// Enhanced React root creation with better mobile deployment error handling
const rootElement = document.getElementById('root');

// Enhanced root element validation for better mobile deployment stability
if (!rootElement) {
  console.error('Root element not found. Mobile deployment may be affected.');
  // Create fallback root element for mobile deployment compatibility
  const fallbackRoot = document.createElement('div');
  fallbackRoot.id = 'root';
  fallbackRoot.style.width = '100%';
  fallbackRoot.style.height = '100%';
  document.body.appendChild(fallbackRoot);
}

// Enhanced React root with StrictMode for performance optimization and mobile deployment
const root = ReactDOM.createRoot(rootElement || document.getElementById('root'));

// Enhanced error boundary wrapper for better mobile deployment error handling
const ErrorBoundary = class extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Mobile deployment error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#059669',
          color: 'white',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>🧀</h1>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Game Loading Error</h2>
          <p style={{ marginBottom: '1rem' }}>
            There was a problem loading the game on mobile.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              fontSize: '1rem',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔄 Reload Game
          </button>
        </div>
      );
    }

    return this.props.children;
  }
};

// Enhanced render with error boundary for better mobile deployment stability
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Enhanced PWA and performance optimizations for mobile deployment
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Enhanced service worker registration for mobile deployment caching
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('🧀 Service Worker registered for mobile deployment:', registration.scope);
      })
      .catch((error) => {
        console.log('Service Worker registration failed for mobile deployment:', error);
      });
    
    console.log('🧀 The Relentless Cheese Hunt Game loaded for mobile deployment!');
  });
}

// Enhanced touch experience optimization for mobile deployment
document.addEventListener('touchstart', function() {
  // Passive touch start for better mobile deployment performance
}, {passive: true});

// Enhanced touch move handling for mobile deployment scroll prevention
document.addEventListener('touchmove', function(e) {
  // Enhanced scroll prevention outside game area for mobile deployment
  if (!e.target.closest('.game-container')) {
    e.preventDefault();
  }
  
  // Enhanced iOS Safari rubber band scrolling prevention for mobile deployment
  if (e.target === document.body || e.target === document.documentElement) {
    e.preventDefault();
  }
}, {passive: false});

// Enhanced keyboard shortcut prevention for better mobile deployment experience
document.addEventListener('keydown', function(e) {
  // Enhanced page refresh prevention for mobile deployment game continuity
  if (e.key === 'F5' || (e.ctrlKey && e.key === 'r') || (e.metaKey && e.key === 'r')) {
    e.preventDefault();
    console.log('Page refresh prevented - mobile deployment game continues!');
  }
  
  // Enhanced zoom shortcut prevention for mobile deployment
  if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0')) {
    e.preventDefault();
  }
  
  // Enhanced space bar scroll prevention for mobile deployment
  if (e.key === ' ' && e.target === document.body) {
    e.preventDefault();
  }
});

// Enhanced context menu prevention for better mobile deployment experience
document.addEventListener('contextmenu', function(e) {
  e.preventDefault();
}, {passive: false});

// Enhanced mobile deployment specific optimizations
const isMobile = navigator.userAgentData?.mobile ?? /Mobi|Android/i.test(navigator.userAgent);

if (isMobile) {
  // Enhanced iOS Safari viewport height fix for mobile deployment
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };
  
  // Enhanced initial viewport height setup for mobile deployment
  setViewportHeight();
  
  // Enhanced viewport height update on orientation change for mobile deployment
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', () => {
    setTimeout(setViewportHeight, 100); // Delay for mobile deployment stability
  });
  
  // Enhanced iOS Safari address bar handling for mobile deployment
  window.addEventListener('scroll', () => {
    if (window.scrollY > 0) {
      window.scrollTo(0, 0);
    }
  }, {passive: true});
  
  // Enhanced mobile deployment performance optimizations
  document.addEventListener('DOMContentLoaded', () => {
    // Enhanced mobile deployment CSS injection for better compatibility
    const mobileStyles = document.createElement('style');
    mobileStyles.textContent = `
      html, body {
        height: 100vh;
        height: -webkit-fill-available;
        overflow: hidden;
        position: fixed;
        width: 100%;
      }
      
      #root {
        height: 100vh;
        height: -webkit-fill-available;
      }
      
      .game-container {
        height: 100vh;
        height: -webkit-fill-available;
        touch-action: none;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
      }
      
      /* Enhanced iOS Safari safe area support for mobile deployment */
      @supports (padding: max(0px)) {
        .game-container {
          padding-left: max(0px, env(safe-area-inset-left));
          padding-right: max(0px, env(safe-area-inset-right));
          padding-top: max(0px, env(safe-area-inset-top));
          padding-bottom: max(0px, env(safe-area-inset-bottom));
        }
      }
    `;
    document.head.appendChild(mobileStyles);
  });
  
  // Enhanced mobile deployment focus management
  document.addEventListener('focusin', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      // Enhanced keyboard avoidance for mobile deployment
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  });
  
  // Enhanced mobile deployment visibility change handling
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Enhanced game pause on mobile deployment background
      console.log('Game paused due to mobile deployment background state');
    } else {
      // Enhanced game resume on mobile deployment foreground
      console.log('Game resumed from mobile deployment background state');
    }
  });
}

// Enhanced performance monitoring for mobile deployment
if (process.env.NODE_ENV === 'development') {
  // Enhanced mobile deployment performance logging
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      console.log(`Mobile deployment performance: ${entry.name} - ${entry.duration}ms`);
    }
  });
  
  observer.observe({ entryTypes: ['measure', 'navigation'] });
}

// Enhanced web vitals reporting for mobile deployment optimization
const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    }).catch(() => {
      console.log('Web vitals not available for mobile deployment monitoring');
    });
  }
};

// Enhanced web vitals monitoring for mobile deployment
reportWebVitals((metric) => {
  console.log(`Mobile deployment web vital: ${metric.name} - ${metric.value}`);
});
